import { Grid } from '@uhg-abyss/web/ui/Grid';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';

import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';

const HoverText = styled('button', {
  color: '#196ecf',
  fontWeight: 'bold',
  fontSize: '$sm',
  fontFamily: 'inherit',
  background: 'none',
  border: 'none',
  padding: '0',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',

  '&:focus-visible': {
    outline: '2px solid #196ecf',
    outlineOffset: '2px',
    borderRadius: '2px',
  },

  '&.commoncategoryHover:hover': {
    textDecoration: 'underline 2px',
    textDecorationThickness: '3px',
    textUnderlineOffset: '0.4em',
    cursor: 'pointer',
  },
});

export type FindhelpCategory = {
  id: number;
  label: string;
  icon?: string; // URL to an icon image
  children?: FindhelpCategory[];
};
function flattenRecursiveCategoryChildren(
  category: FindhelpCategory
): FindhelpCategory[] {
  if (!category.children) return [];

  return category.children?.flatMap((subcategory: FindhelpCategory) => {
    return [subcategory, ...flattenRecursiveCategoryChildren(subcategory)];
  });
}
export function prettyTextToKebabCase(text: string): string {
  return text.toLowerCase().replace(/[\s/]/g, '-').replace(/&/g, 'and');
}
export type CommonModalProps = {
  currentTopLevelCategory: string;

  onClickCategoryFilter: (clicked: FindhelpCategory, parent: string) => void;

  isOpen: boolean;

  setIsOpen: (isOpen: boolean) => void;

  categoryData: FindhelpCategory[];
};

export const CommonModal = ({
  categoryData,
  currentTopLevelCategory,
  isOpen,
  setIsOpen,
  onClickCategoryFilter,
}: CommonModalProps) => {
  const currentCategoryData: FindhelpCategory | undefined = useMemo(() => {
    return currentTopLevelCategory !== null
      ? categoryData.find((category) => {
          return category.label === currentTopLevelCategory;
        })
      : undefined;
  }, [currentTopLevelCategory, categoryData]);

  const flattenedCategoryData: FindhelpCategory[] = useMemo(() => {
    return currentCategoryData
      ? flattenRecursiveCategoryChildren(currentCategoryData).sort((a, b) => {
          return a.label.localeCompare(b.label);
        })
      : [];
  }, [currentCategoryData]);

  const [filterByQueryValue, setFilterByQueryValue] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredCategories = flattenedCategoryData.filter((subcategory: FindhelpCategory) =>
    filterByQueryValue !== ''
      ? subcategory.label.toLowerCase().includes(filterByQueryValue.toLowerCase())
      : true
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!filterByQueryValue) {
      setStatusMessage('');
      return;
    }
    const count = filteredCategories.length;
    debounceRef.current = setTimeout(() => {
      if (count === 0) {
        setStatusMessage('No results found');
      } else if (count === 1) {
        setStatusMessage(`1 result found: ${filteredCategories[0].label}`);
      } else {
        setStatusMessage(`${count} results found`);
      }
    }, 1000);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filterByQueryValue]);

  return (
    <React.Fragment>
      <ModalDialog
        header={currentTopLevelCategory}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setFilterByQueryValue('');
        }}
        css={{
          'abyss-modal-content-container': {
            padding: '16px',
          },
          'abyss-modal-base-header-container': {
            borderBottom: 'none',
          },
        }}
      >
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
        >
          {statusMessage}
        </div>
        <div>
          <TextInput
            label={`Filter ${currentCategoryData?.label ?? ''} Needs`}
            data-cy="filter-categories-input"
            value={filterByQueryValue}
            placeholder="Type to filter"
            onChange={(e: any) => {
              return setFilterByQueryValue(e.target.value);
            }}
            onClear={() => {
              return setFilterByQueryValue('');
            }}
          />
        </div>

        <div role="group" aria-label={`Filter ${currentCategoryData?.label ?? ''} Needs`}>
          <Grid
            style={{
              margin: '0px',
            }}
          >
            {/* 'All' is a special case and will always take up a full visual row */}
            <Grid.Col
              span={{
                xs: 12,
              }}
            >
              <HoverText
                className="commoncategoryHover"
                onClick={() => {
                  onClickCategoryFilter(currentCategoryData!, '');
                  setFilterByQueryValue('');
                  setIsOpen(false);
                }}
                data-cy={`select-all-${prettyTextToKebabCase(
                  currentCategoryData?.label ?? ''
                )}-filters-btn`}
              >
                All
              </HoverText>
            </Grid.Col>
            {filteredCategories
              .map((subcategory: FindhelpCategory) => {
                return (
                  <Grid.Col key={subcategory.id}>
                    <HoverText
                      className="commoncategoryHover"
                      onClick={() => {
                        onClickCategoryFilter(
                          subcategory,
                          currentCategoryData?.label ?? ''
                        );
                        setFilterByQueryValue('');
                        setIsOpen(false);
                      }}
                      data-cy={`select-${prettyTextToKebabCase(
                        subcategory.label
                      )}-filters-btn`}
                    >
                      {subcategory.label.charAt(0).toUpperCase() +
                        subcategory.label.slice(1).toLowerCase()}
                    </HoverText>
                  </Grid.Col>
                );
              }, [])}
          </Grid>
        </div>
      </ModalDialog>
    </React.Fragment>
  );
};